import React, { useState } from 'react';
import type { SignatureValidationResultDto } from '../../services/documentSignatureService';
import {
  hasEmbeddedSignatures,
  hasRegistryMetadata,
  integrityBadge,
  isUploadedOriginalWithoutEmbeddedSignatures,
  padesChainBadge,
  padesIntegrityBadge,
  resolveSignatureKind,
  showSigningClaimsForUploadedFile,
  signatureKindBadge,
  signerStatusLabel,
  statusLabel,
  validationSummaryMessage,
} from './validatePageUtils';

interface ValidatePageResultProps {
  result: SignatureValidationResultDto;
  onVerifyAnother: () => void;
}

export const ValidatePageResult: React.FC<ValidatePageResultProps> = ({
  result,
  onVerifyAnother,
}) => {
  const [showUploadedHash, setShowUploadedHash] = useState(false);
  const [showProcessSigners, setShowProcessSigners] = useState(false);
  const integrity = integrityBadge(result.integrityStatus);
  const summaryMessage = validationSummaryMessage(result);
  const showRegistry = hasRegistryMetadata(result);
  const showPades = hasEmbeddedSignatures(result);
  const uploadedOriginalOnly = isUploadedOriginalWithoutEmbeddedSignatures(result);
  const showSigningClaims = showSigningClaimsForUploadedFile(result);

  return (
    <div className="mt-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Resultado</h2>
        <button
          type="button"
          onClick={onVerifyAnother}
          className="text-sm font-medium text-blue-600 hover:text-blue-800"
        >
          Verificar outro documento
        </button>
      </div>

      {integrity && (
        <p className={`rounded-lg border px-4 py-3 text-sm ${integrity.className}`}>{integrity.label}</p>
      )}

      {summaryMessage && (
        <p className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800">
          {summaryMessage}
        </p>
      )}

      {showPades && result.embeddedSignatures && (
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Assinaturas digitais no PDF
          </h3>
          <ul className="mt-3 space-y-3">
            {result.embeddedSignatures.map((sig) => {
              const integrityPades = padesIntegrityBadge(sig.signatureIntact);
              const chainPades = padesChainBadge(sig.chainValid);
              const holder = sig.certificateSubject || sig.signerName || sig.signatureFieldName;
              return (
                <li
                  key={sig.signatureFieldName}
                  className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm"
                >
                  <p className="font-medium text-slate-900">{holder}</p>
                  {sig.signingTime && (
                    <p className="mt-1 text-xs text-slate-500">
                      {new Date(sig.signingTime).toLocaleString('pt-BR')}
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span
                      className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${integrityPades.className}`}
                    >
                      {integrityPades.label}
                    </span>
                    <span
                      className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${chainPades.className}`}
                    >
                      {chainPades.label}
                    </span>
                  </div>
                  {sig.errors.length > 0 && (
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-slate-600">
                      {sig.errors.map((err) => (
                        <li key={err}>{err}</li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {result.uploadedSha256 && (
        <div className="text-xs text-slate-600">
          <button
            type="button"
            onClick={() => setShowUploadedHash((v) => !v)}
            className="font-medium text-blue-600 hover:text-blue-800"
          >
            {showUploadedHash ? 'Ocultar' : 'Exibir'} SHA-256 do arquivo enviado
          </button>
          {showUploadedHash && (
            <p className="mt-2 break-all font-mono text-slate-500">{result.uploadedSha256}</p>
          )}
        </div>
      )}

      {showRegistry ? (
        <>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
            <p>
              <span className="font-medium text-slate-700">Documento:</span> {result.documentTitle}
            </p>
            <p className="mt-1">
              <span className="font-medium text-slate-700">ID público:</span>{' '}
              <span className="break-all font-mono text-xs text-slate-600">{result.publicId}</span>
            </p>
            <p className="mt-1">
              <span className="font-medium text-slate-700">Status da solicitação:</span>{' '}
              {statusLabel(result.status)}
            </p>
            <p className="mt-2 break-all font-mono text-xs text-slate-600">
              <span className="font-sans font-medium text-slate-700">SHA-256 do PDF original:</span>
              <br />
              {result.originalBinarySha256}
            </p>
            {!uploadedOriginalOnly && result.finalDocumentSha256 && (
              <p className="mt-2 break-all font-mono text-xs text-slate-600">
                <span className="font-sans font-medium text-slate-700">SHA-256 do documento final:</span>
                <br />
                {result.finalDocumentSha256}
              </p>
            )}
            {showSigningClaims && result.barukSealApplied && (
              <p className="mt-3 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
                Documento final selado digitalmente por Baruk Tecnologia LTDA (certificado ICP-Brasil e-CNPJ).
              </p>
            )}
            {uploadedOriginalOnly && (
              <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Para validar as assinaturas digitais, envie o documento final assinado.
              </p>
            )}
          </div>

          {uploadedOriginalOnly ? (
            <div className="text-xs text-slate-600">
              <button
                type="button"
                onClick={() => setShowProcessSigners((v) => !v)}
                className="font-medium text-blue-600 hover:text-blue-800"
              >
                {showProcessSigners ? 'Ocultar' : 'Ver'} signatários do processo
              </button>
              {showProcessSigners && (
                <ul className="mt-3 divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm">
                  {result.signers.map((s) => (
                    <li
                      key={`${s.email}-${s.name}`}
                      className="flex flex-wrap justify-between gap-3 px-4 py-3"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900">{s.name}</p>
                        <p className="text-slate-500">{s.email}</p>
                      </div>
                      <div className="text-right text-slate-600">
                        <p>{signerStatusLabel(s.status)}</p>
                        {s.signedAt && (
                          <p className="text-xs">{new Date(s.signedAt).toLocaleString('pt-BR')}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Signatários</h3>
              <ul className="mt-3 divide-y divide-slate-100 rounded-lg border border-slate-200">
                {result.signers.map((s) => {
                  const kindBadge = signatureKindBadge(
                    resolveSignatureKind(s),
                    result.barukSealApplied ?? false
                  );
                  return (
                    <li
                      key={`${s.email}-${s.name}`}
                      className="flex flex-wrap justify-between gap-3 px-4 py-3 text-sm"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900">{s.name}</p>
                        <p className="text-slate-500">{s.email}</p>
                        <span
                          className={`mt-2 inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${kindBadge.className}`}
                        >
                          {kindBadge.label}
                        </span>
                      </div>
                      <div className="text-right text-slate-600">
                        <p>{signerStatusLabel(s.status)}</p>
                        {s.signedAt && (
                          <p className="text-xs">{new Date(s.signedAt).toLocaleString('pt-BR')}</p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};
